import { Dispatch, SetStateAction, useState, useEffect, useMemo } from 'react';
import { Placement, State, createPopper } from '@popperjs/core';
import { useMeasure } from '.';

export type { Placement as PopoutPlacement };
export type { State as PopoutState };

export interface IArrowConfiguration {
  /** Defines the size of the arrow */
  arrowSize?: number;
  /** Background color to be used by arrow */
  backgroundColor?: string;
  /** Border color to be used by arrow */
  borderColor?: string;
  /** Defines the size of the arrow border */
  borderSize?: number;
  /** Defines if the popout is enabled */
  enabled?: boolean;
  /** Defines the offset value for the arrow */
  offset?: number;
}

export interface IPopoutConfiguration {
  /** Defines if the popout is enabled */
  enabled?: boolean;
  /** Defines the configuration of the arrow */
  arrow?: IArrowConfiguration;
  /** Defines the offset value for the popout */
  offset?: number;
  /**
   * Indicates if popout will prevent overflow
   * Note: This can cause issues when popout size is the same as the reference.
   */
  preventOverflow?: boolean;
}

const DEFAULT_ARROW_SIZE = 8;
const DEFAULT_PADDING = 12;

export const usePopout = (
  /** Reference of the element from which the popout is going to be shown */
  reference: HTMLElement | null,
  /** Placement where the popout is going to be shown */
  placement: Placement,
  /** Defines the configuration of the popout */
  configuration?: IPopoutConfiguration
): {
  setPopoutElement: Dispatch<SetStateAction<HTMLElement | null>>;
  setArrowElement: Dispatch<SetStateAction<HTMLElement | null>>;
  popoutAttributes?: { [key: string]: string | boolean };
  state?: State;
} => {
  const [popoutElement, setPopoutElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
  const {
    offset,
    enabled,
    preventOverflow = true,
    arrow: arrowConfiguration,
  } = configuration || {};
  const [state, setState] = useState<State>();
  const [ref, { width }] = useMeasure<HTMLElement>();
  const renderPopout = useMemo(
    () => enabled && reference != null && popoutElement != null && width !== 0,
    [enabled, popoutElement, reference, width]
  );

  useEffect(() => {
    reference && ref(reference);
  }, [ref, reference]);

  useEffect(() => {
    if (renderPopout) {
      createPopper(reference as HTMLElement, popoutElement as HTMLElement, {
        placement,
        strategy: 'fixed',
        // @ts-ignore
        onFirstUpdate: (state: State) => {
          setState(state);
          if (arrowElement) {
            arrowElement.setAttribute('class', 'arrow');
            arrowElement.setAttribute('aria-hidden', 'true');
          }
        },
        modifiers: [
          {
            name: 'arrow',
            enabled: !!arrowElement,
            options: {
              element: arrowElement,
              padding: offset ? 0 : DEFAULT_PADDING,
            },
          },
          {
            name: 'preventOverflow',
            enabled: preventOverflow,
          },
          {
            name: 'offset',
            options: {
              offset: [
                0,
                arrowConfiguration?.arrowSize
                  ? arrowConfiguration?.arrowSize +
                    arrowConfiguration?.arrowSize / 2
                  : arrowElement
                    ? DEFAULT_PADDING
                    : DEFAULT_ARROW_SIZE,
              ],
            },
          },
        ],
      });
    }
  }, [
    arrowConfiguration?.arrowSize,
    arrowElement,
    offset,
    placement,
    popoutElement,
    preventOverflow,
    ref,
    reference,
    renderPopout,
  ]);

  return {
    setPopoutElement,
    setArrowElement,
    state,
  };
};
